import React, { useCallback, useEffect, useState } from 'react';
import { FiTrendingUp, FiMail, FiMessageCircle, FiPhone, FiZap } from 'react-icons/fi';
import { formatMoney } from '../utils/money';
import { currentMonthRange } from '../utils/collectionExport';
import PlanUpgradeCard from './PlanUpgradeCard';

const CHANNEL_META = {
  email: { label: 'Email', icon: FiMail },
  whatsapp: { label: 'WhatsApp', icon: FiMessageCircle },
  llamado: { label: 'Llamadas', icon: FiPhone },
};

function ImpactSkeleton({ box, modoOscuro }) {
  const pulse = modoOscuro ? 'bg-gray-800 animate-pulse' : 'bg-gray-200 animate-pulse';
  return (
    <div className={`rounded-2xl border p-4 space-y-3 ${box}`}>
      <div className={`h-4 w-48 rounded ${pulse}`} />
      <div className={`h-10 w-40 rounded ${pulse}`} />
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`h-14 rounded-xl ${pulse}`} />
        ))}
      </div>
    </div>
  );
}

export default function CollectionImpactPanel({
  secureFetch,
  hasProfesional,
  modoOscuro,
  box,
  textPri,
  textSec,
  textMuted,
  compact = false,
  rango = null,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const green = modoOscuro ? 'text-green-300' : 'text-green-700';
  const tile = modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50';

  const fetchImpact = useCallback(async () => {
    if (!hasProfesional) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const range = rango || currentMonthRange();
      const res = await secureFetch(
        `/api/reportes/cobranzas-impacto?desde=${range.desde}&hasta=${range.hasta}`
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'No pudimos cargar el impacto de cobranza');
      setData(json);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasProfesional, rango, secureFetch]);

  useEffect(() => {
    fetchImpact();
  }, [fetchImpact]);

  if (!hasProfesional) {
    return (
      <PlanUpgradeCard
        compact
        description="Medí cuánto cobraste después de recordatorios y gestiones con plan Profesional+."
        planLabel="Profesional"
        modoOscuro={modoOscuro}
      />
    );
  }

  if (loading) return <ImpactSkeleton box={box} modoOscuro={modoOscuro} />;

  if (error) {
    return (
      <div className={`rounded-2xl border px-4 py-3 text-sm ${box} ${modoOscuro ? 'text-red-300' : 'text-red-700'}`}>
        {error}
      </div>
    );
  }

  if (!data?.summary) return null;

  const { summary, byChannel, recentHits, attributionDays } = data;
  const hasRecovery = summary.recoveredArs > 0;
  const channels = Object.entries(byChannel?.recoveredArs || {})
    .filter(([, amount]) => amount > 0);

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`}>
      <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
            modoOscuro ? 'bg-green-900/40 text-green-200' : 'bg-green-50 text-green-800'
          }`}>
            <FiTrendingUp className="w-3 h-3" />
            Impacto de cobranza
          </span>
          {summary.gestionesOutreach > 0 && (
            <span className={`text-[10px] ${textMuted}`}>
              {summary.gestionesOutreach} gestión(es) en el período
            </span>
          )}
        </div>
        <h2 className={`text-sm font-semibold ${textPri}`}>
          {hasRecovery
            ? `Recuperaste ${formatMoney(summary.recoveredArs, 'ARS')} con tus gestiones`
            : 'Todavía no hay cobros atribuidos a gestiones este mes'}
        </h2>
        <p className={`text-xs mt-0.5 ${textSec}`}>
          {hasRecovery
            ? `${summary.paymentsAttributed} pago(s) en ${summary.invoicesRecovered} comprobante(s) dentro de ${attributionDays} días de un recordatorio.`
            : `Cuando un cliente paga dentro de ${attributionDays} días de un email, WhatsApp o llamada, lo vas a ver acá.`}
        </p>
      </div>

      <div className="p-4 space-y-3">
        <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
          <div className={`rounded-xl px-3 py-2.5 ${tile} ${hasRecovery ? (modoOscuro ? 'ring-1 ring-green-800/60' : 'ring-1 ring-green-200') : ''}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Recuperado</div>
            <div className={`text-lg font-semibold tabular-nums mt-0.5 ${hasRecovery ? green : textPri}`}>
              {formatMoney(summary.recoveredArs, 'ARS')}
            </div>
          </div>
          <div className={`rounded-xl px-3 py-2.5 ${tile}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Pagos atribuidos</div>
            <div className={`text-lg font-semibold tabular-nums mt-0.5 ${textPri}`}>
              {summary.paymentsAttributed}
            </div>
          </div>
          {!compact && (
            <>
              <div className={`rounded-xl px-3 py-2.5 ${tile}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Gestiones</div>
                <div className={`text-lg font-semibold tabular-nums mt-0.5 ${textPri}`}>
                  {summary.gestionesOutreach}
                </div>
              </div>
              <div className={`rounded-xl px-3 py-2.5 ${tile}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Tasa atribución</div>
                <div className={`text-lg font-semibold tabular-nums mt-0.5 ${textPri}`}>
                  {summary.paymentsTotal > 0 ? `${summary.attributionRate}%` : '—'}
                </div>
              </div>
            </>
          )}
        </div>

        {channels.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {channels.map(([channel, amount]) => {
              const meta = CHANNEL_META[channel] || { label: channel, icon: FiZap };
              const Icon = meta.icon;
              return (
                <span
                  key={channel}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium ${
                    modoOscuro ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 opacity-70" />
                  {meta.label}: {formatMoney(amount, 'ARS')}
                </span>
              );
            })}
          </div>
        )}

        {!compact && recentHits?.length > 0 && (
          <div>
            <p className={`text-[10px] font-semibold uppercase tracking-wide mb-2 ${textMuted}`}>
              Últimos cobros atribuidos
            </p>
            <ul className={`divide-y text-xs ${modoOscuro ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {recentHits.slice(0, 5).map((hit) => (
                <li key={`${hit.invoiceId}-${hit.pagoFecha}-${hit.montoArs}`} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className={`font-medium ${textPri}`}>N°{hit.numero}</span>
                    <span className={`${textMuted}`}> · {hit.cliente}</span>
                    <span className={`block text-[10px] mt-0.5 ${textMuted}`}>
                      {hit.gestionTipo}{hit.automatico ? ' automático' : ''} · {hit.pagoFecha}
                    </span>
                  </div>
                  <span className={`shrink-0 font-semibold tabular-nums ${green}`}>
                    +{formatMoney(hit.montoArs, 'ARS')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
